import DOMPurify from "dompurify";
import { Marked, type Tokens } from "marked";

import { miminPath, miminRef } from "./entityPath";

// Markdown for svar fra «Spør Claude». Egen Marked-instans, så den globale
// konfigurasjonen i misc/Markdown.tsx (autolenker, breaks) ikke påvirkes.
//
// Lenker på formen mimin://{type}/{id} blir interne lenker: href peker på
// appens rute (via `hrefFor`, som kjenner routerens base), og
// data-mimin-path bærer stien så panelet kan navigere uten sideinnlasting.
// Eksterne http(s)-lenker åpnes i ny fane; alt annet vises som ren tekst.
//
// Med `sources` blir en mimin-lenke bare lenke når posten står blant
// verktøyresultatene (PRD US27.2); ellers vises teksten uten lenke.

export const CURSOR_HTML =
  '<span class="ask-cursor" aria-hidden="true"></span>';

const escapeAttr = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const createMarked = (
  hrefFor: (path: string) => string,
  current: { sources?: ReadonlySet<string> },
) => {
  const marked = new Marked({ gfm: true, breaks: false });
  marked.use({
    renderer: {
      link(
        this: { parser: { parseInline: (t: Tokens.Link["tokens"]) => string } },
        token: Tokens.Link,
      ) {
        const text = this.parser.parseInline(token.tokens);
        const path = miminPath(token.href);
        const ref = miminRef(token.href);
        if (path && current.sources && ref && !current.sources.has(ref)) {
          return text;
        }
        if (path) {
          return `<a href="${escapeAttr(hrefFor(path))}" data-mimin-path="${escapeAttr(path)}">${text}</a>`;
        }
        if (/^https?:\/\//i.test(token.href)) {
          return `<a href="${escapeAttr(token.href)}" target="_blank" rel="noopener noreferrer">${text}</a>`;
        }
        return text;
      },
    },
  });
  return marked;
};

// Lager en renderer én gang per hrefFor (panelet memoiserer den), siden den
// kalles på nytt for hvert token mens svaret strømmer.
export const createAskMarkdown = (hrefFor: (path: string) => string) => {
  const current: { sources?: ReadonlySet<string> } = {};
  const marked = createMarked(hrefFor, current);
  return (
    source: string,
    options: { cursor?: boolean; sources?: ReadonlySet<string> } = {},
  ): string => {
    current.sources = options.sources;
    const html = marked.parse(
      options.cursor ? `${source}${CURSOR_HTML}` : source,
      { async: false },
    ) as string;
    return DOMPurify.sanitize(html, { ADD_ATTR: ["target"] });
  };
};
